import { Component, OnDestroy, OnInit, input } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { CsIconModule } from '@components/icon';
import { BroadcastService } from '@services/broadcast-service';
import { CartService } from '@services/cart-service/cart.service';
import { ConnectionService } from '@services/connection-service/index';
import { CartUpdateOptions } from '@shared/types/cart.types';
import { fromActions } from '@store/actions';
import { fromSelectors } from '@store/selectors';
import { AppConfig } from '../../app.config';
import {
  RecommendedProduct,
  RecommendedProductCollection,
  RecommendedCartLineItem,
} from './recommended-products.types';

@Component({
  selector: 'recommended-products',
  templateUrl: './recommended-products.html',
  standalone: true,
  imports: [CsIconModule],
  host: { class: 'tw-block' },
})
export class RecommendedProductsComponent implements OnInit, OnDestroy {
  readonly sectionName = input<string>('recommendedProducts');
  readonly productLimit = input<number>(10);

  private readonly collectionName: string = 'recommendedProducts';
  private readonly taggedCardsDisplayType: string = 'TAGGED_CARDS';
  readonly cartAction = this.appConfig.Shared.Cart.Action;

  loading: boolean = true;
  displayType: string = '';
  products: RecommendedProduct[] = [];
  productsInCart: Record<string, number> = {};
  cartItems: RecommendedCartLineItem[] = [];
  private isCartOperationPending: boolean = false;
  private subscriptions: Subscription[] = [];

  constructor(
    private conn: ConnectionService,
    private cartService: CartService,
    private broadcast: BroadcastService,
    private store: Store,
    private appConfig: AppConfig,
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      const collection: RecommendedProductCollection = await this.conn.fetchRecommendedProducts(this.productLimit());
      this.displayType = collection?.displayType || '';
      this.products = (collection?.products || []).filter((product: RecommendedProduct): boolean => !!product?.price);
      if (this.products.length) await this.getCart();
    } catch {
      this.products = [];
    } finally {
      this.loading = false;
    }

    this.subscriptions.push(
      this.store.select(fromSelectors.selectCartProducts)
        .subscribe((products: Record<string, number>): void => { this.productsInCart = { ...products }; }),
      this.broadcast.on('REFRESH_SHOP_CART').subscribe((): void => { this.getCart(); }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
  }

  // Single boolean the template gates the whole section on.
  get isSectionVisible(): boolean {
    return !this.loading && !!this.products.length && this.displayType === this.taggedCardsDisplayType;
  }

  async getCart(): Promise<void> {
    const cart = await this.conn.getCart();
    this.cartItems = cart?.get('lineItems') || [];
    const quantities: Record<string, number> = {};
    this.cartItems.forEach((item: RecommendedCartLineItem): void => { quantities[item.productId] = item.quantity; });
    this.productsInCart = quantities;
  }

  productImage(product: RecommendedProduct): string {
    return product?.rebrandedImageWithBackground?.[0]
      || product?.productUnboxedImage?.[0]
      || product?.images?.[0]
      || product?.productImage;
  }

  hasDiscount(product: RecommendedProduct): boolean {
    return !!product?.mrp && product.mrp > product.price;
  }

  quantityInCart(product: RecommendedProduct): number {
    return this.productsInCart[product?.objectId] || 0;
  }

  isInCart(product: RecommendedProduct): boolean {
    return this.quantityInCart(product) > 0;
  }

  async updateCart(product: RecommendedProduct, action: string, event: Event, image?: HTMLImageElement): Promise<void> {
    event.stopPropagation();
    if (this.isCartOperationPending) return;
    this.isCartOperationPending = true;
    try {
      const options: CartUpdateOptions = {
        img: image,
        productsInCart: this.productsInCart || {},
        cartItems: this.cartItems || [],
        section: this.sectionName(),
      };
      const result = await this.cartService.addOrRemoveFromCart(product, action, options);
      if (result.success) {
        if (result.updatedQuantity > 0) this.productsInCart[product.objectId] = result.updatedQuantity;
        else delete this.productsInCart[product.objectId];
        this.store.dispatch(fromActions.CartUpdateProductsBegin({ products: this.productsInCart }));
        await this.getCart();
      }
    } finally {
      this.isCartOperationPending = false;
    }
  }

  viewProduct(product: RecommendedProduct): void {
    this.conn.navigateToURL(`/product/${product.objectId}?section=${this.sectionName()}`);
  }

  viewAll(): void {
    // Preserve the origin tab so the reorder page's back button returns the user
    // to where they came from (home vs shop), not always to shop.
    const source = this.sectionName().startsWith('home') ? 'home' : 'shop';
    this.conn.navigateToURL(`/user/order/reorder?from=${this.collectionName}&type=collection&source=${source}`);
  }
}
